import { Fragment, useEffect, useRef, useState } from "react";
import api from "../../../services/api";
import Autocomplete from "../../common/Autocomplete";
import { toast } from "react-toastify";
import { normalizList } from "../../../utils/utils";
import Modal from "../../common/Modal";
import Switch from "react-switch";
import { useTranslation } from "react-i18next";
import { confirmMissingCpt, isMissingCpt } from "./qcodeMissingCpt";

const init = {
  key: "",
  area: 0,
  areaWasted: 0,
  description: "",
  price: 0,
  reimbursement: 0,
  icd10: [],
  cpt: [],
};

const defaultsCPT = [
  {
    code: "15271",
    description:
      "Application of skin substitute graft to trunk, arms, legs, total wound surface area up to 100 sq cm; first 25 sq cm or less wound surface area.",
  },
  {
    code: "15272",
    description:
      "Each additional 25 sq cm wound surface area, or part thereof (List separately in addition to code for primary procedure).",
  },
  {
    code: "15273",
    description:
      "Application of skin substitute graft to trunk, arms, legs, total wound surface area greater than 100 sq cm; first 100 sq cm wound surface area.",
  },
  {
    code: "15274",
    description:
      "Each additional 100 sq cm wound surface area, or part thereof (List separately in addition to code for primary procedure).",
  },
  {
    code: "15275",
    description:
      "Application of skin substitute graft to face, scalp, eyelids, mouth, neck, ears, orbits, genitalia, hands, feet, and/or multiple digits; first 25 sq cm or less wound surface area.",
  },
  {
    code: "15276",
    description:
      "Each additional 25 sq cm wound surface area, or part thereof (List separately in addition to code for primary procedure).",
  },
  {
    code: "15277",
    description:
      "Application of skin substitute graft to face, scalp, eyelids, mouth, neck, ears, orbits, genitalia, hands, feet, and/or multiple digits; greater than 100 sq cm, first 100 sa cm.",
  },
  {
    code: "15278",
    description:
      "Each additional 100 sa cm wound surface area, or part thereof (List separately in addition to code for primary procedure).",
  },
];

// The editor works on its own deep copy. Several handlers below edit the cpt /
// icd10 arrays in place, and a shallow copy shares those arrays with the saved
// note -- or, for a Q code missing the field, with `init`, which every Q code
// opened later in the session starts from. Either way an unsaved edit escaped.
const toEditable = (qcode) => JSON.parse(JSON.stringify({ ...init, ...(typeof qcode === "string" ? {} : qcode) }));

export default function EditQCode({ onSave, qcode = init, onChange, chat }) {
  const { t } = useTranslation();
  const [qcodesOptions, setQcodesOptions] = useState([]);
  const [qCode, setQCode] = useState(() => toEditable(qcode));
  const [icd10] = useState(normalizList(chat?.contextVariables["local:billableCodesICD10"] || []));
  const [cpt] = useState(normalizList(chat?.contextVariables["local:inOfficeProcedures"] || []));
  const [showModalManageICD, setShowModalManageICD] = useState(false);

  // save() waits on a confirm dialog that renders in its own root and can stay open
  // indefinitely. These let it see the present when the provider finally answers,
  // rather than the render it started in.
  const mounted = useRef(true);
  const chatIdRef = useRef(chat?.id);
  const onSaveRef = useRef(onSave);
  chatIdRef.current = chat?.id;
  onSaveRef.current = onSave;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    setQCode(toEditable(qcode));
  }, [qcode]);

  useEffect(() => {
    if (onChange) {
      onChange(normalize(qCode));
    }
    // eslint-disable-next-line
  }, [qCode]);

  useEffect(() => {
    const fetchData = async () => {
      let res = await api.getQcodes();
      setQcodesOptions(
        res.data.result.map((item) => {
          // Format
          return {
            key: item.c,
            label: item.c + " - " + item.d + " - " + item.p + "/cm2",
            price: item.p,
            description: item.d,
          };
        })
      );
    };

    fetchData();
  }, []);

  const save = async () => {
    let code = qcodesOptions.find((q) => q.key === qCode.key);

    if (!code) {
      return toast.error(t("qcode.pleaseSelectCode"));
    }
    if (!qCode.area) {
      return toast.error(t("qcode.pleaseEnterArea"));
    }

    const chatIdAtSave = chat?.id;
    if (!(await confirmMissingCpt(qCode, t))) {
      return;
    }

    // onSave writes to whichever note is open now, so a "Save anyway" answered
    // after the note changed would put this Q code on the wrong patient.
    if (!mounted.current || chatIdRef.current !== chatIdAtSave) {
      return toast.warn(t("chatEdit.qcodes.notSavedNoteChanged"));
    }

    if (!qCode.areaWasted) {
      qCode.areaWasted = 0;
    }

    onSaveRef.current(normalize(qCode));
  };

  const normalize = (qCode) => {
    let code = qcodesOptions.find((q) => q.key === qCode.key);

    if (code) {
      qCode.code = code.key;
      qCode.description = code.description;
      qCode.price = code.price;
      qCode.reimbursement = parseFloat(code.price) * parseFloat(qCode.area);
    }
    qCode.area = parseFloat(qCode.area);
    qCode.areaWasted = parseFloat(qCode.areaWasted);
    qCode.date = new Date();

    return qCode;
  };

  const setValue = (field, value) => {
    let updated = JSON.parse(JSON.stringify(qCode));
    updated[field] = value;
    setQCode(updated);
  };

  const toggleCPT = (cptCode, checked) => {
    // Always a new array: qCode.cpt can be the saved Q code's own array, or the
    // module-level init.cpt for a Q code with no cpt field, so a push would leak
    // an unsaved toggle into the note or into the next Q code opened.
    const current = qCode.cpt || [];
    const list = checked
      ? [...current, { code: cptCode.code, description: cptCode.description, icd10: [] }]
      : current.filter((c) => c.code !== cptCode.code);
    setValue("cpt", list);
  };

  const toggleICD = (icdCode, checked) => {
    // A new array, so closing the picker without Save really discards the toggles.
    const current = showModalManageICD?.icd10 || [];
    const list = checked
      ? [...current, { code: icdCode.code, description: icdCode.description }]
      : current.filter((c) => c.code !== icdCode.code);
    setShowModalManageICD({ ...showModalManageICD, icd10: list });
  };

  const saveICD = () => {
    if (!showModalManageICD.cpt) {
      // General ICD10 for the QCode
      let list = qCode.cpt || [];
      let index = list.findIndex((c) => c.code === showModalManageICD.code);
      list[index].icd10 = showModalManageICD.icd10;
      setValue("cpt", list);
      setShowModalManageICD(false);
    } else {
      // ICD10 for a specific CPT
      showModalManageICD.cpt = showModalManageICD.cpt.map((cpt) => {
        cpt.icd10 = cpt.icd10?.filter((i) => showModalManageICD.icd10.find((s) => s.code === i.code));
        return cpt;
      });

      setValue("icd10", [...showModalManageICD.icd10]);
      setShowModalManageICD(false);
    }
  };

  const makeMain = (code) => {
    showModalManageICD?.icd10?.map((item, i) => {
      if (item.code === code) {
        item.main = true;
      } else {
        item.main = false;
      }
      return item;
    });
    setShowModalManageICD({ ...showModalManageICD });
  };

  const removeMain = (index) => {
    qCode.icd10[index].main = false;
    setQCode({ ...qCode });
  };

  return (
    <div className="qcodes-section">
      <div className="add-q-code mb-30">
        <div className="row-form">
          <label>{t("chatEdit.labels.code")}</label>
          <Autocomplete
            initValue={qCode?.key && qCode.key + " - " + qCode.description}
            onSelect={(e) => setValue("key", e?.key)}
            data={qcodesOptions}
          />
        </div>

        <div className="row-form">
          <label>
            {t("chatEdit.qcodes.area")} (cm<sup>2</sup>)
          </label>
          <input
            min={0}
            value={qCode.area}
            type="number"
            onChange={(e) => setValue("area", Number(e.target.value).toString())}
          ></input>
        </div>

        <div className="row-form">
          <label>
            {t("chatEdit.qcodes.areaWasted")} (cm<sup>2</sup>)
          </label>
          <input
            min={0}
            value={qCode.areaWasted}
            type="number"
            onChange={(e) => setValue("areaWasted", Number(e.target.value).toString())}
          ></input>
        </div>

        {isMissingCpt(qCode) && <div className="badge light-orange mb-10">{t("chatEdit.qcodes.noCptAttached")}</div>}

        {/* TABLE ICD CODES */}
        <div className="table-qcode">
          <div className="table-qcode-header">
            <div className="table-qcode-column">
              <div className="table-qcode-row">{t("chatEdit.qcodes.cptHeader")}</div>
            </div>
            <div className="table-qcode-column">
              <div className="table-qcode-row">{t("chatEdit.qcodes.icd10Header")}</div>
            </div>
          </div>
          <div className="table-qcode-body">
            {/* CPT CODES */}
            <div className="table-qcode-column">
              <div className="table-qcode-row" key={"code_all"}>
                <p>{t("chatEdit.labels.allIcd10Codes")}</p>
              </div>
              {[...cpt, ...defaultsCPT]?.map((cpt, index) => (
                <div className="table-qcode-row" key={"code_" + index}>
                  <Switch
                    checked={qCode?.cpt?.find((c) => c.code === cpt.code) ? true : false}
                    onChange={(e) => toggleCPT(cpt, e)}
                    uncheckedIcon={false}
                    checkedIcon={false}
                    className="mr-10"
                    height={24}
                    width={45}
                  />
                  <p>
                    <b>{cpt?.code}</b>&nbsp;- {cpt?.description}
                  </p>
                </div>
              ))}
            </div>

            {/* ICD for CPTS */}
            <div className="table-qcode-column">
              <div className="table-qcode-row">
                <button className="small blue no-border round ml-0 mr-10" onClick={() => setShowModalManageICD(qCode)}>
                  <i className="notranslate material-icons">edit</i>
                </button>
                <p>
                  {qCode?.icd10?.length === 0
                    ? t("chatEdit.qcodes.noIcd10Codes")
                    : qCode?.icd10
                        ?.sort((a, b) => (a.main ? -1 : 1))
                        .map((icd10, index) => (
                          <Fragment key={"icd10_" + index}>
                            {icd10.main ? <b>{icd10.code}&nbsp;</b> : <>{icd10.code}&nbsp;</>}
                          </Fragment>
                        ))}
                </p>
              </div>
              {[...cpt, ...defaultsCPT]?.map((cptAll, index) => (
                <div className="table-qcode-row" key={"icd10_" + index}>
                  {(() => {
                    let cpt = qCode?.cpt?.find((c) => c.code === cptAll.code);
                    if (cpt) {
                      return (
                        <>
                          <button className={"small  no-border round ml-0 mr-10 blue"} onClick={() => setShowModalManageICD(cpt)}>
                            <i className="notranslate material-icons">edit</i>
                          </button>
                          <p>
                            {!cpt.icd10 || cpt.icd10?.length === 0
                              ? t("chatEdit.qcodes.noIcd10ForCpt")
                              : cpt?.icd10?.map((icd10, index) => icd10.code).join(", ")}
                          </p>
                        </>
                      );
                    }
                  })()}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showModalManageICD && (
        <Modal
          close={() => setShowModalManageICD(false)}
          title={showModalManageICD.code + " - " + showModalManageICD.description}
          size="medium"
          show={true}
        >
          {/* TABLE ICD CODES */}
          <div className="table-qcode col-1">
            <div className="table-qcode-header">
              <div className="table-qcode-column">
                <div className="table-qcode-row">{t("chatEdit.qcodes.icd10Header")}</div>
              </div>
            </div>
            <div className="table-qcode-body">
              {/* CPT CODES */}
              <div className="table-qcode-column">
                {(showModalManageICD.cpt ? icd10 : qCode.icd10)?.map((icdActual, index) => {
                  let icd = showModalManageICD?.icd10?.find((i) => i.code === icdActual.code);
                  return (
                    <div className="table-qcode-row" key={"code_" + index}>
                      <Switch
                        checked={icd ? true : false}
                        onChange={(e) => toggleICD(icdActual, e)}
                        uncheckedIcon={false}
                        checkedIcon={false}
                        className="mr-10"
                        height={24}
                        width={45}
                      />
                      {showModalManageICD.cpt && (
                        <>
                          {icd ? (
                            <>
                              {icd?.main ? (
                                <button className="small blue no-border round ml-0 mr-10" onClick={() => removeMain(index)}>
                                  <i className="notranslate material-icons-outlined">star_rate</i>
                                </button>
                              ) : (
                                <button
                                  className="small white no-border round ml-0 mr-10"
                                  onClick={() => makeMain(icd.code)}
                                  data-tooltip-id="my-tooltip"
                                  data-tooltip-content={t("chatEdit.qcodes.makePrimary")}
                                  data-tooltip-place="top-center"
                                  data-tooltip-class-name={"tooltip"}
                                >
                                  <i className="notranslate material-icons-outlined">star_rate</i>
                                </button>
                              )}
                            </>
                          ) : (
                            <>
                              <button className="small gray no-border round ml-0 mr-10 hidden" disabled>
                                <i className="notranslate material-icons-outlined">star_rate</i>
                              </button>
                            </>
                          )}
                        </>
                      )}

                      <p>
                        <b>{icdActual?.code}</b>&nbsp;- {icdActual?.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <button className="btn" onClick={() => saveICD()}>
              {t("common.save")}
            </button>
          </div>
        </Modal>
      )}

      {/* Embedded hosts (TableViewQCodes) pass onChange only and save through their own modal. */}
      {onSave && (
        <div className="modal-actions">
          <button className="btn" onClick={() => save()}>
            {t("common.save")}
          </button>
        </div>
      )}
    </div>
  );
}
